import {Http} from '../../../Data/config/Http';
import {StoreDTO, StoreStatisticsDTO, StoreGeoLocationDTO} from '../models/store.dto';
import {Bounds} from '../../../Presentation/components/common/types';
import {API_ROUTES} from '../../../constants/apiRoutes';
import type {BaseResponse} from '../../../Data/types/BaseResponse';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {unwrapResponse, handleDataSourceError} from '../../../utils/errorHelpers';

export const StoreAPIDataSource = {
  getAll: async (page = 1, limit: number = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE, filters?: {city?: string; region?: string; search?: string}): Promise<{ items: StoreDTO[]; pagination?: any }> => {
    try {
      const params: Record<string, string | number> = {page, limit};
      if (filters?.city) {params.city = filters.city;}
      if (filters?.region) {params.region = filters.region;}
      // Backend GET /stores supports ?search= (ILIKE on store_name + phone_number).
      if (filters?.search) {params.search = filters.search;}
      const response = await Http.get<BaseResponse<StoreDTO[]>>(API_ROUTES.STORES.BASE, {params});
      const raw = response.data;
      const items = unwrapResponse(raw, 'حدث خطأ في جلب المتاجر');
      return {
        items: Array.isArray(items) ? items : [],
        pagination: raw.pagination
      };
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  // دالة جلب المتاجر في Bounding Box - محدثة لتطابق الباك إند
  getNearbyInBounds: async (bounds: Bounds, includeDetails: boolean = false): Promise<StoreDTO[]> => {
    try {
      const response = await Http.get<BaseResponse<StoreDTO[]>>(API_ROUTES.STORES.NEARBY, {
        params: {
          sw_lat: bounds.sw.lat,
          sw_lng: bounds.sw.lng,
          ne_lat: bounds.ne.lat,
          ne_lng: bounds.ne.lng,
          limit: 1000,
          includeDetails
        }
      });
      const items = unwrapResponse(response.data, 'حدث خطأ في جلب المتاجر');
      return Array.isArray(items) ? items : [];
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  // دالة للحصول على المتاجر للخريطة - تستخدم endpoint خاص بالخريطة
  getStoresForMap: async (bounds?: Bounds): Promise<StoreDTO[]> => {
    try {
      // Try using the nearby endpoint with includeDetails instead of map endpoint
      if (bounds) {
        const response = await Http.get<BaseResponse<StoreDTO[]>>(API_ROUTES.STORES.NEARBY, {
          params: {
            sw_lat: bounds.sw.lat,
            sw_lng: bounds.sw.lng,
            ne_lat: bounds.ne.lat,
            ne_lng: bounds.ne.lng,
            limit: 1000,
            includeDetails: true
          }
        });
        const items = unwrapResponse(response.data, 'حدث خطأ في جلب المتاجر');
        return Array.isArray(items) ? items : [];
      } else {
        // Fallback to original map endpoint
        const response = await Http.get<BaseResponse<StoreDTO[]>>(API_ROUTES.STORES.MAP, {
          params: {format: 'map', includeDetails: true}
        });
        const items = unwrapResponse(response.data, 'حدث خطأ في جلب المتاجر');
        return Array.isArray(items) ? items : [];
      }
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  getById: async (id: string): Promise<StoreDTO> => {
    try {
      const response = await Http.get<BaseResponse<StoreDTO>>(API_ROUTES.STORES.BY_ID(id));
      return unwrapResponse(response.data, 'حدث خطأ في جلب تفاصيل المتجر');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  /** موقع جغرافي لمتجر واحد (للخريطة) - يتضمن حقل address */
  getGeoLocation: async (id: string): Promise<StoreGeoLocationDTO> => {
    try {
      const response = await Http.get<BaseResponse<StoreGeoLocationDTO>>(API_ROUTES.STORES.GEO_LOCATION(id));
      return unwrapResponse(response.data, 'حدث خطأ في جلب موقع المتجر');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  create: async (store: StoreDTO): Promise<StoreDTO> => {
    try {
      const response = await Http.post<BaseResponse<StoreDTO>>(API_ROUTES.STORES.BASE, store);
      return unwrapResponse(response.data, 'فشل في إضافة المتجر', [200, 201]);
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  update: async (id: string, store: StoreDTO): Promise<StoreDTO> => {
    try {
      // Check if we have file uploads
      const hasFileUploads = store.commercialRegisterImageFile ||
                            store.taxCardImageFile ||
                            store.profileImageFile ||
                            (store.storeImagesFiles && store.storeImagesFiles.length > 0);

      let response;

      if (hasFileUploads) {
        // Use FormData for file uploads
        const formData = new FormData();

        // Add text fields
        if (store.name) {formData.append('store_name', store.name);}
        if (store.managerName) {formData.append('manager_name', store.managerName);}
        if (store.developerId != null) {formData.append('developer_id', store.developerId.toString());}
        if (store.phoneNumber) {formData.append('phone_number', store.phoneNumber);}
        if (store.latitude !== undefined) {formData.append('latitude', store.latitude.toString());}
        if (store.longitude !== undefined) {formData.append('longitude', store.longitude.toString());}
        if (store.city) {formData.append('city', store.city);}
        if (store.region) {formData.append('region', store.region);}
        if (store.notes) {formData.append('notes', store.notes);}
        if (store.commercialRegisterNumber) {formData.append('commercial_register_number', store.commercialRegisterNumber);}
        if (store.taxCardNumber) {formData.append('tax_card_number', store.taxCardNumber);}

        // Add removed images information
        if ((store as any).removedImages) {
          const removedImages = (store as any).removedImages;
          if (removedImages.commercialRegisterImage) {
            formData.append('remove_commercial_register_image', 'true');
          }
          if (removedImages.taxCardImage) {
            formData.append('remove_tax_card_image', 'true');
          }
          if (removedImages.profileImage) {
            formData.append('remove_profile_image', 'true');
          }
          if (removedImages.storeImages && removedImages.storeImages.length > 0) {
            removedImages.storeImages.forEach((imageUrl: string) => {
              formData.append('remove_store_images', imageUrl);
            });
          }
        }

        // Add file uploads
        if (store.commercialRegisterImageFile) {
          formData.append('commercial_register_image', store.commercialRegisterImageFile);
        }
        if (store.taxCardImageFile) {
          formData.append('tax_card_image', store.taxCardImageFile);
        }
        if (store.profileImageFile) {
          formData.append('profile_image', store.profileImageFile);
        }
        if (store.storeImagesFiles && store.storeImagesFiles.length > 0) {
          store.storeImagesFiles.forEach((file, index) => {
            formData.append('store_images', file);
          });
        }

        response = await Http.put<BaseResponse<StoreDTO>>(API_ROUTES.STORES.BY_ID(id), formData);
      } else {
        // Convert camelCase fields to snake_case for JSON updates
        const snakeCaseStore = {
          store_name: store.name,
          manager_name: store.managerName,
          developer_id: store.developerId ?? undefined,
          phone_number: store.phoneNumber,
          latitude: store.latitude,
          longitude: store.longitude,
          city: store.city,
          region: store.region,
          notes: store.notes,
          commercial_register_number: store.commercialRegisterNumber,
          tax_card_number: store.taxCardNumber,
          // Include removed images information for JSON updates
          ...((store as any).removedImages && {
            remove_commercial_register_image: (store as any).removedImages.commercialRegisterImage ? 'true' : undefined,
            remove_tax_card_image: (store as any).removedImages.taxCardImage ? 'true' : undefined,
            remove_profile_image: (store as any).removedImages.profileImage ? 'true' : undefined,
            remove_store_images: (store as any).removedImages.storeImages || []
          })
        };

        // Filter out undefined values
        const cleanStore = Object.fromEntries(
          Object.entries(snakeCaseStore).filter(([_, value]) => value !== undefined)
        );

        response = await Http.put<BaseResponse<StoreDTO>>(API_ROUTES.STORES.BY_ID(id), cleanStore);
      }

      return unwrapResponse(response.data, 'فشل في تعديل المتجر');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      const response = await Http.delete<BaseResponse<void>>(API_ROUTES.STORES.BY_ID(id));
      unwrapResponse(response.data, 'فشل في حذف المتجر', [200, 204]);
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  // دالة إضافة دين للمتجر
  addDebt: async (storeId: string, debtData: { amount: number; description?: string }): Promise<any> => {
    try {
      const response = await Http.post<BaseResponse<any>>(API_ROUTES.STORES.ADD_DEBT(storeId), debtData);
      return unwrapResponse(response.data, 'فشل في إضافة الدين للمتجر', [200, 201]);
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  // دالة جلب إحصائيات المتاجر
  getStatistics: async (): Promise<StoreStatisticsDTO> => {
    try {
      const url = API_ROUTES.STORES.STATISTICS;
      const response = await Http.get<BaseResponse<StoreStatisticsDTO>>(url);
      return unwrapResponse(response.data, 'فشل جلب إحصائيات المتاجر');
    } catch (error) {
      handleDataSourceError(error);
    }
  }
};
